import type { EntrySide, HardwareInteraction, TableHole } from './hardwareInteractionsRun';
import { isLeftRight, type PartItem } from './partsRun';
import { sameUnit, type StepHole } from './stepHoles';

// WHAT A JOINT SHOULD HAVE, AGAINST WHAT FIND HOLES FOUND IN IT.
//
// The hardware table says it, line by line: "Right side rail · front · 2 × blind ↔ Front legs
// frame". Find holes places those holes; this reads its answer back against the table, so a
// joint says it is right rather than having to be looked at to find out.
//
// NOT A DISTANCE BETWEEN AXES. A line's holes are worked out by running the hardware's line
// through the parts where the product built them (see holeLines), so two holes of one line are
// in line by construction. What can go wrong is the line not REACHING the part the table says it
// continues into — and that is what `reaches` asks.

export type LineCheck = {
  key: string;
  // Which entry of a mirror pair the line is from (see EntrySide).
  side: EntrySide;
  line: TableHole;
  // The holes found for it.
  holes: StepHole[];
  // A through hole found blind, or the other way round.
  wrongKind: boolean;
  // On a line said to continue into another part: whether each hole's line holes that part too.
  // Null where there is nothing to ask, or a hole says no line (an answer from before it did).
  reaches: boolean | null;
  ok: boolean;
};

export type JointCheck = {
  lines: LineCheck[];
  // Found, and no line of the table accounts for it.
  extra: StepHole[];
  // Lines not right, and holes nobody expected.
  issues: number;
};

export function checkJoint(
  rows: HardwareInteraction[],
  found: StepHole[],
  items: PartItem[],
): JointCheck {
  const used = new Set<string>();
  const lineOf = (h: StepHole) => (h.line ? found.filter((o) => o.line === h.line) : null);
  const lines = rows.flatMap((row, r) => row.holes.filter((l) => l.kind !== 'none').map((line, k): LineCheck => {
    const pair = isLeftRight(items[Number(line.part)]);
    let holes = found.filter((h) => !used.has(h.id) && h.part === line.part && h.via === line.for
      && (!pair || sameUnit(h.side, row.side)));
    // ONE HAND'S SHARE OF A PART WITH NO HAND: the seat both rails screw into takes both hands'
    // screws, and each entry of the pair expects its own — the holes whose line runs on into its
    // hand's rail. Not where no hole says its line: then there is nothing to split them by.
    if (!pair && row.side && holes.some((h) => h.line)) {
      holes = holes.filter((h) => lineOf(h)?.some((o) => o.side === row.side));
    }
    holes.forEach((h) => used.add(h.id));
    const wrongKind = line.kind !== 'cross' && holes.some((h) => h.through !== (line.kind === 'through'));
    const reaches = !line.aligned || line.kind === 'cross' || !holes.length || holes.some((h) => !h.line)
      ? null
      : holes.every((h) => lineOf(h)!.some((o) => o.id !== h.id && o.part === line.aligned));
    return {
      key: `${r}-${k}`,
      side: row.side,
      line,
      holes,
      wrongKind,
      reaches,
      ok: holes.length === line.count && !wrongKind && reaches !== false,
    };
  }));
  const extra = found.filter((h) => !used.has(h.id));
  return { lines, extra, issues: lines.filter((l) => !l.ok).length + extra.length };
}

// AND THE COVERS THE JOINT'S ROWS NAME — a wood button on a screw, a felt pad on a foot — against
// what the covers call placed: how many the rows use, how many it put down, and, on the joint on
// screen, which of them go into a part and how deep (see coverCollisions). `placed` null where
// the joint's covers have not been placed at all.
export type CoverLine = {
  id: string;
  expected: number | null;
  placed: number | null;
  into: { part: string; mm: number }[];
  ok: boolean;
};

export function checkCovers(
  rows: HardwareInteraction[],
  placements: { cover: string }[] | null,
  into: (cover: string) => { part: string; mm: number }[] = () => [],
): CoverLine[] {
  const ids = [...new Set(rows.flatMap((r) => r.covers))];
  return ids.map((id) => {
    const said = rows.filter((r) => r.covers.includes(id)).map((r) => r.counts[id]);
    const expected = said.some((n) => n != null) ? said.reduce<number>((a, n) => a + (n ?? 0), 0) : null;
    const placed = placements ? placements.filter((p) => p.cover === id).length : null;
    const deep = into(id);
    return {
      id, expected, placed, into: deep,
      ok: placed != null && (expected == null || placed === expected) && !deep.length,
    };
  });
}
